import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    max: { type: Number, default: 120 }
  }

  connect() {
    this.frameId = null
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.handleScroll = this.handleScroll.bind(this)
    this.update = this.update.bind(this)

    window.addEventListener("scroll", this.handleScroll, { passive: true })
    window.addEventListener("resize", this.handleScroll)

    if (this.reducedMotion.addEventListener) {
      this.reducedMotion.addEventListener("change", this.handleScroll)
    } else {
      this.reducedMotion.addListener(this.handleScroll)
    }

    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
    window.removeEventListener("resize", this.handleScroll)

    if (this.reducedMotion.removeEventListener) {
      this.reducedMotion.removeEventListener("change", this.handleScroll)
    } else {
      this.reducedMotion.removeListener(this.handleScroll)
    }

    if (this.frameId) {
      window.cancelAnimationFrame(this.frameId)
    }
  }

  handleScroll() {
    if (this.frameId) return

    this.frameId = window.requestAnimationFrame(this.update)
  }

  update() {
    this.frameId = null
    const wordState = this.wordState()

    if (this.reducedMotion.matches) {
      this.setParallax(0, wordState.opacity, 0, 0)
      return
    }

    const rect = this.element.getBoundingClientRect()
    const distance = Math.max(-rect.top, 0)
    const base = Math.min(this.maxValue, distance * 0.2)

    this.setParallax(base, wordState.opacity, wordState.flash, wordState.red)
  }

  wordState() {
    const about = this.element.querySelector("#about")
    if (!about) return { opacity: 0.28, flash: 0, red: 0 }

    const aboutTop = about.getBoundingClientRect().top
    const fadeStart = window.innerHeight * 0.64
    const fadeEnd = window.innerHeight * -0.54
    const progress = this.clamp((fadeStart - aboutTop) / (fadeStart - fadeEnd))
    const redStart = 0.02
    const redPeak = 0.14
    const settleEnd = 0.42

    if (progress <= redPeak) {
      const rise = this.clamp((progress - redStart) / (redPeak - redStart))
      const easedRise = rise * rise

      return {
        opacity: this.lerp(0.28, 0.92, easedRise),
        flash: easedRise,
        red: easedRise
      }
    }

    const settle = this.clamp((progress - redPeak) / (settleEnd - redPeak))
    const easedSettle = settle * settle * (3 - 2 * settle)

    return {
      opacity: this.lerp(0.92, 0.3, easedSettle),
      flash: 1 - easedSettle,
      red: 1 - easedSettle
    }
  }

  clamp(value) {
    return Math.max(0, Math.min(1, value))
  }

  lerp(start, end, progress) {
    return start + (end - start) * progress
  }

  setParallax(base, opacity, flash, red) {
    const redAmount = this.clamp(red)
    const color = [
      Math.round(this.lerp(136, 244, redAmount)),
      Math.round(this.lerp(136, 81, redAmount)),
      Math.round(this.lerp(136, 81, redAmount))
    ]

    this.element.style.setProperty("--parallax-word-y", `${(base * 1.06).toFixed(2)}px`)
    this.element.style.setProperty("--parallax-word-opacity", opacity.toFixed(3))
    this.element.style.setProperty("--parallax-word-flash", flash.toFixed(3))
    this.element.style.setProperty("--parallax-word-color", `rgb(${color.join(" ")})`)
    this.element.style.setProperty("--parallax-word-glow-rgb", color.join(" "))
    this.element.style.setProperty("--parallax-mark-y", `${(base * -0.2).toFixed(2)}px`)
    this.element.style.setProperty("--parallax-orbit-y", `${(base * 0.22).toFixed(2)}px`)
  }
}
